import React, { useState } from "react";
import {
  Typography,
  Stack,
  TextField,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  useTheme,
} from "@mui/material";
import { Close as CloseIcon, Save as SaveIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import contactService from "../../../services/contactService";
import { Contact } from "../../../types/crm/types/contact";
import { useAuth } from "../../../context/AuthContext";

const ContactCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [open, setOpen] = useState(true);
  const { userToken } = useAuth();
  const activeProfile = userToken?.user.profiles.find((profile) => profile.roleId === 3);

  const [form, setForm] = useState<Contact>({
    fullName: "",
    phone: "",
    type: "",
    email: "",
    address: "",
    userProfileId: activeProfile?.id ?? 0,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof Contact, string>>>(
    {}
  );
  const [saving, setSaving] = useState(false);

  const handleClose = () => {
    setOpen(false);
    navigate(-1);
  };

  const handleChange = (field: keyof Contact, value: string) => {
    setForm({ ...form, [field]: value });
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateForm = () => {
    const newErrors: Partial<Record<keyof Contact, string>> = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[0-9]{8,15}$/;

    if (!form.fullName.trim())
      newErrors.fullName = "El nombre completo es requerido";
    if (!form.phone.trim()) {
      newErrors.phone = "El teléfono es requerido";
    } else if (!phoneRegex.test(form.phone)) {
      newErrors.phone = "El teléfono debe tener entre 8 y 15 dígitos";
    }

    if (!form.type.trim()) newErrors.type = "El tipo de contacto es requerido";

    if (!form.email?.trim()) {
      newErrors.email = "El correo electrónico es requerido";
    } else if (!emailRegex.test(form.email)) {
      newErrors.email = "El correo electrónico no es válido";
    }

    if (!form.address?.trim()) newErrors.address = "La dirección es requerida";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);

      const formData = new FormData();
      formData.append("FullName", form.fullName);
      formData.append("Phone", form.phone);
      formData.append("Type", form.type);
      formData.append("Email", form.email ?? "");
      formData.append("Address", form.address ?? "");
      formData.append("UserProfileId", form.userProfileId.toString());

      await contactService.createContact(formData);

      alert("Contacto creado con éxito");
      navigate(-1);
    } catch (error) {
      console.error("Error al crear el contacto:", error);
      alert("Ocurrió un error al guardar el contacto");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          bgcolor: "rgba(255, 255, 255, 0.95)",
          borderRadius: 3,
          backdropFilter: "blur(8px)",
          boxShadow: theme.shadows[5],
          p: 3,
        },
      }}
    >
      <DialogTitle>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: "bold", color: theme.palette.primary.main }}
          >
            Crear Nuevo Contacto
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          <TextField
            required
            label="Nombre completo"
            value={form.fullName}
            onChange={(e) => handleChange("fullName", e.target.value)}
            fullWidth
            error={!!errors.fullName}
            helperText={errors.fullName}
          />
          <TextField
            required
            label="Teléfono"
            value={form.phone}
            onChange={(e) => handleChange("phone", e.target.value)}
            fullWidth
            error={!!errors.phone}
            helperText={errors.phone}
          />
          <TextField
            required
            label="Tipo de contacto"
            value={form.type}
            onChange={(e) => handleChange("type", e.target.value)}
            fullWidth
            error={!!errors.type}
            helperText={errors.type}
          />
          <TextField
            required
            label="Correo electrónico"
            value={form.email}
            onChange={(e) => handleChange("email", e.target.value)}
            fullWidth
            error={!!errors.email}
            helperText={errors.email}
          />
          <TextField
            required
            label="Dirección"
            value={form.address}
            onChange={(e) => handleChange("address", e.target.value)}
            fullWidth
            error={!!errors.address}
            helperText={errors.address}
          />

          <Stack direction="row" justifyContent="flex-end" spacing={2} mt={2}>
            <Button variant="outlined" onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              Guardar
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default ContactCreatePage;
